import type * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { connect } from 'react-redux'
import type { ConnectedProps } from 'react-redux'

import { useAccount } from '~publish/contexts/AccountContext'
import { useOrganizationSwitcher } from '~publish/components/MainSidebar/components/OrganizationSwitcher/useOrganizationSwitcher'
import MissingAccessPage from '~publish/legacy/missing-access-page'
import { actions as orgActions } from '~publish/legacy/organizations'
import type { RootState } from '~publish/legacy/store'

interface OwnProps {
  children: ReactNode
}

interface StateProps {
  reduxReady: boolean
  hasCurrentOrg: boolean
}

interface DispatchProps {
  storeSelectedOrg: (currentOrgId: string) => void
}

/**
 * The org id captured from a `/org/:id` deep-link at module-load time.
 *
 * `/org/:id` is not a real page route — it falls through to the catch-all
 * redirect in `AppPages`, which navigates away (to `/all-channels`) on the very
 * first render, before account data is ready. So by the time we can actually
 * apply the switch the URL no longer matches and any value read with `useMatch`
 * is already gone. `LoadingGate` is also remounted as the app shell settles
 * (feature-flag resolution, sidebar swaps), so a component-local ref doesn't
 * survive either.
 *
 * Reading the path once at import time — before React mounts and before any
 * navigation — captures the id durably. It is consumed (cleared) the first time
 * a switch is applied so manual org changes afterwards are never overridden.
 */
const ORG_DEEP_LINK_PATTERN = /^\/org\/([^/?#]+)/
export const matchDeepLinkOrgId = (pathname: string): string | null =>
  ORG_DEEP_LINK_PATTERN.exec(pathname)?.[1] ?? null

let pendingDeepLinkOrgId: string | null =
  typeof window !== 'undefined'
    ? matchDeepLinkOrgId(window.location.pathname)
    : null

/**
 * Decides whether a captured `/org/:id` deep-link should trigger an org switch.
 *
 * Returns the org id to switch to, or `null` when the deep-link should be a
 * no-op: there is no pending id, the data needed to validate it has not loaded
 * yet, the org is not one the account belongs to, or it is the org already
 * selected. Kept pure so the (timing-sensitive) decision is unit-testable.
 */
export const resolveOrgDeepLinkTarget = ({
  pendingOrgId,
  currentOrganizationId,
  memberOrgIds,
  isReady,
}: {
  pendingOrgId: string | null
  currentOrganizationId: string | null
  memberOrgIds: readonly string[]
  isReady: boolean
}): string | null => {
  if (!isReady || !pendingOrgId || !currentOrganizationId) {
    return null
  }
  if (pendingOrgId === currentOrganizationId) {
    return null
  }
  if (!memberOrgIds.includes(pendingOrgId)) {
    return null
  }
  return pendingOrgId
}

/**
 * We use this component to delay rendering children until we have
 * enough data loaded into the state.
 */
const mapStateToProps = (state: RootState): StateProps => {
  const orgsLoaded = state.organizations.loaded
  const profilesLoaded = state.profileSidebar.loaded
  const hasCurrentOrg = !!state.organizations?.selected

  return {
    reduxReady: orgsLoaded && profilesLoaded,
    hasCurrentOrg,
  }
}

const mapDispatchToProps: DispatchProps = {
  storeSelectedOrg: (currentOrgId: string) =>
    orgActions.setCurrentOrganization(currentOrgId),
}
const connector = connect(mapStateToProps, mapDispatchToProps)

/**
 * Exposes the pending `/org/:id` deep-link target (if any) and a `consume`
 * callback that clears it once the switch has been applied. State is seeded
 * from the module-level capture so it survives the catch-all redirect and any
 * remounts of `LoadingGate`.
 */
const usePendingDeepLinkOrgId = (): {
  pendingOrgId: string | null
  consume: () => void
} => {
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(
    pendingDeepLinkOrgId,
  )

  const consume = useCallback(() => {
    pendingDeepLinkOrgId = null
    setPendingOrgId(null)
  }, [])

  return { pendingOrgId, consume }
}

/**
 * Applies a `/org/:id` deep-link by switching the current organization, once,
 * on load.
 *
 * Switching goes through the publish `useOrganizationSwitcher` — the same path
 * the in-app org switcher uses — which runs the `SetCurrentOrganization`
 * mutation (persisting server-side), refetches account/sidebar data and
 * navigates home. The org is server-validated; we additionally only attempt the
 * switch for an org the account is actually a member of and that differs from
 * the org already selected, so a stale or self-referential link is a no-op.
 */
const useApplyOrgDeepLink = (): void => {
  const { pendingOrgId, consume } = usePendingDeepLinkOrgId()
  const { account, loading: accountLoading } = useAccount()
  const {
    organizations,
    loading: organizationsLoading,
    error: organizationsError,
    switchOrganization,
  } = useOrganizationSwitcher()
  // Guard against a second attempt while the first switch is in flight.
  const isSwitchingRef = useRef(false)

  // The org the account is currently on. Compare the deep-link against this
  // (not the switcher's value, which can momentarily read null before the
  // account query resolves and cause a needless self-switch).
  const currentOrganizationId = account?.currentOrganization?.id ?? null

  useEffect(() => {
    if (!pendingOrgId || isSwitchingRef.current) {
      return
    }

    // Wait until both the account and the org list are loaded so the
    // membership and "already selected" checks are reliable. Bail out (without
    // consuming) while the org-list query is in error so a transient fetch
    // failure doesn't discard a valid deep-link before Apollo can refetch.
    const isReady =
      !accountLoading &&
      !organizationsLoading &&
      !organizationsError &&
      !!currentOrganizationId
    if (!isReady) {
      return
    }

    const targetOrgId = resolveOrgDeepLinkTarget({
      pendingOrgId,
      currentOrganizationId,
      memberOrgIds: organizations.map((org) => org.id),
      isReady,
    })

    // Either way the deep-link is handled now — consume it so it never retries.
    consume()

    if (targetOrgId) {
      isSwitchingRef.current = true
      void switchOrganization(targetOrgId).finally(() => {
        isSwitchingRef.current = false
      })
    }
  }, [
    pendingOrgId,
    accountLoading,
    organizationsLoading,
    organizationsError,
    currentOrganizationId,
    organizations,
    switchOrganization,
    consume,
  ])
}

type PropsFromRedux = ConnectedProps<typeof connector>
type Props = OwnProps & PropsFromRedux

const LoadingGate: React.FC<Props> = ({
  reduxReady = false,
  children,
  storeSelectedOrg,
  hasCurrentOrg,
}: Props) => {
  const user = useAccount()
  const selectedOrgInAccount = user?.account?.currentOrganization?.id
  const canAccessPublishing =
    user?.account?.currentOrganization?.billing?.canAccessPublishing

  // Honour a `/org/:id` deep-link by switching orgs on load (see hook docs).
  useApplyOrgDeepLink()

  // Keep the legacy Redux org selection in sync with the account's current
  // organization. When a deep-link switch happens above, the account refetch
  // updates `selectedOrgInAccount`, so this naturally follows along.
  useEffect(() => {
    if (reduxReady && selectedOrgInAccount && hasCurrentOrg) {
      storeSelectedOrg(selectedOrgInAccount)
    }
  }, [selectedOrgInAccount, hasCurrentOrg, reduxReady, storeSelectedOrg])

  useEffect(() => {
    // Select the organization in Redux once we have fetched the user.
    // This is only used on the first render.
    if (
      reduxReady &&
      !user?.loading &&
      selectedOrgInAccount &&
      !hasCurrentOrg
    ) {
      storeSelectedOrg(selectedOrgInAccount)
    }
  }, [
    selectedOrgInAccount,
    hasCurrentOrg,
    reduxReady,
    storeSelectedOrg,
    user?.loading,
  ])

  if (!user.loading && canAccessPublishing === false) {
    return <MissingAccessPage />
  }

  return children
}

export default connector(LoadingGate)
